import {
  ChatChannel,
  ChatDirection,
  IChatConversation,
  IChatMessage,
  MetaChannel,
  SendStatus,
} from '@dataTypes/chat.type'
import moment from 'moment'
import { getFullName } from './fullname.util'
import { ICalls, ICallStatus, ICallType } from '@dataTypes/calls.type'
import { ChatState } from '@store/reducers/chat.reducer'
import { recordingBlobNameFromRef } from './call-recording-playback.util'
import { logger } from '@utils/safeLogger'

// Map Twilio SMS status to chat send_status enum
export const mapTwilioToSendStatus = (status?: string): SendStatus => {
  switch ((status || '').trim().toLowerCase()) {
    case 'sent':
      return SendStatus.sent
    case 'delivered':
      return SendStatus.delivered
    case 'undelivered':
      return SendStatus.undelivered
    case 'failed':
      return SendStatus.failed
    case 'queued':
    case 'accepted':
    case 'scheduled':
    case 'sending':
      return SendStatus.queued
    default:
      return SendStatus.delivery_unknown
  }
}

export const messageCountsAsUnread = (
  message: Pick<IChatMessage, 'is_read' | 'chat_direction'>,
): boolean =>
  !message.is_read && message.chat_direction === ChatDirection.incoming

export const sortConversationByDate = (
  conversation: IChatConversation[],
  key?: keyof IChatConversation,
): IChatConversation[] => {
  const sortedData = conversation.sort((a, b) => {
    if (key) {
      return (
        new Date(b[key] as string).getTime() -
        new Date(a[key] as string).getTime()
      )
    }

    // Default: sort by last message timestamp
    let _timestampA: string = a.date_time_created_at as string
    if (a.chat_message && a.chat_message.length > 0) {
      _timestampA = a.chat_message[a.chat_message.length - 1].timestamp
    }
    let _timestampB: string = b.date_time_created_at as string
    if (b.chat_message && b.chat_message.length > 0) {
      _timestampB = b.chat_message[b.chat_message.length - 1].timestamp
    }

    // Get the timestamp to compare for conversation A
    const timestampA = _timestampA ? new Date(_timestampA).getTime() : 0

    // Get the timestamp to compare for conversation B
    const timestampB = _timestampB ? new Date(_timestampB).getTime() : 0

    return timestampB - timestampA
  })
  return sortedData.map((el) => ({
    ...el,
    chat_participant: el.chat_participant.map((p) => {
      const username =
        p.username ??
        (getFullName(p.contact).length > 0
          ? getFullName(p.contact)
          : getFullName(p.user?.provider_profile).length > 0
            ? `Dr. ${getFullName(p.user?.provider_profile)}`
            : getFullName(p.user).length > 0
              ? getFullName(p.user)
              : (p.user?.username ?? undefined))
      return {
        ...p,
        username,
        photo:
          p.photo ??
          p.contact?.photo ??
          p.user?.provider_profile?.photo ??
          undefined,
      }
    }),
    chat_message:
      el.chat_message?.map((m) => {
        const participant = el.chat_participant.find(
          (p) => p.chat_participant_id === m.sender_id,
        )
        const first_name =
          participant?.contact?.first_name ?? participant?.username

        return {
          ...m,
          sender:
            m.sender ??
            (m.chat_direction === ChatDirection.outgoing
              ? { first_name: 'You' }
              : m.chat_direction === 'AI'
                ? { first_name: 'AI receptionist' }
                : {
                    first_name,
                    middle_name: participant?.contact?.middle_name,
                    last_name: participant?.contact?.last_name,
                  }),
        }
      }) ?? [],
  }))
}

export const getSenderType = (
  chatType: string | null,
): 'clinic' | 'client' | 'ai' => {
  if (chatType === 'Outgoing') return 'clinic'
  else if (chatType == 'AI') return 'ai'
  else return 'client'
}

export const getCallLog = (
  content: string,
  sender: string,
  patientName: string,
): string => {
  if (content.includes('isMissedCall')) {
    switch (sender) {
      case 'You':
        return `${patientName} missed a call from you.`
      default:
        return `You missed a call from ${sender}`
    }
  } else if (
    content.includes('isCalling') ||
    content.includes('isRinging') ||
    content.includes('isInProgressCall')
  ) {
    switch (sender) {
      case 'You':
        return `You are calling.`
      default:
        return `${sender} is calling.`
    }
  } else if (content.includes('isCompletedCall')) {
    return 'The audio call ended.'
  } else return ''
}

export const getCompletedCallAudioUrl = (
  content?: string,
  recordingUrl?: string | null,
): string => {
  const fromContent = content?.includes(':::::')
    ? content.split(':::::').slice(1).join(':::::')
    : ''
  return (
    recordingBlobNameFromRef(recordingUrl) ||
    recordingBlobNameFromRef(fromContent) ||
    recordingUrl ||
    fromContent ||
    ''
  )
}

export const getLastActive = (_date: string | Date): string => {
  const date = new Date(_date)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) return 'Active now' // Less than a minute
  if (diffInSeconds === 60) return 'Active 1 min ago' // Exactly 1 minute
  if (diffInSeconds < 3600)
    return `Active ${Math.floor(diffInSeconds / 60)} mins ago` // Less than an hour
  if (diffInSeconds === 3600) return 'Active 1 hr ago' // Exactly 1 hour
  if (diffInSeconds < 86400)
    return `Active ${Math.floor(diffInSeconds / 3600)} hrs ago` // Less than a day

  return 'Active days ago'
}

export const getPhoneCallerConversationId = (
  conversations: IChatConversation[],
  caller: Partial<ICalls>,
): string | null => {
  const response = conversations.find((convo) =>
    convo.chat_participant.length > 0
      ? convo.chat_participant[0].phone_number === caller.caller_number ||
        convo.chat_participant[0].phone_number === caller.receiver_number
      : false,
  )
  return response?.chat_conversation_id ?? null
}

export const getPhoneCallerMessageId = (
  conversations: IChatConversation[],
  caller: Partial<ICalls>,
  content: string,
): string | null => {
  const convo = conversations.find((el) =>
    el.chat_participant.length > 0
      ? el.chat_participant[0].phone_number === caller.caller_number ||
        el.chat_participant[0].phone_number === caller.receiver_number
      : false,
  )

  const response = convo?.chat_message.find((el) => el.content === content)
  return response?.chat_message_id ?? null
}

export const getOutgoingChatStatus = (
  chat: IChatMessage,
  trackMessageStatus = true,
): string => {
  if (!trackMessageStatus) return ''
  if (chat.send_status) {
    if (chat.send_status === 'queued') return 'sending...'
    else if (
      ['delivery_unknown', 'undelivered', 'failed'].includes(chat.send_status)
    )
      return 'sending failed.'
    else if (['sent', 'delivered'].includes(chat.send_status))
      return 'message sent'
  }
  return 'message sent'
}

export const getCallLogKeyword = (
  currentStatus: ICallStatus,
  callType: ICallType | undefined,
  newStatus: ICallStatus,
): string[] => {
  switch (newStatus) {
    case 'Ringing':
      return ['[CHAT-CALL-LOG]::{isCalling}', '[CHAT-CALL-LOG]::{isRinging}']
    case 'In Progress':
      return [
        '[CHAT-CALL-LOG]::{isCalling}',
        '[CHAT-CALL-LOG]::{isInProgressCall}',
      ]
    case 'Completed': {
      if (currentStatus === 'Ringing' && callType === 'Incoming') {
        return [
          '[CHAT-CALL-LOG]::{isCalling}',
          '[CHAT-CALL-LOG]::{isMissedCall}',
        ]
      } else if (currentStatus === 'In Progress' && callType === 'Incoming') {
        return [
          '[CHAT-CALL-LOG]::{isInProgressCall}',
          '[CHAT-CALL-LOG]::{isCompletedCall}:::::',
        ]
      } else
        return [
          '[CHAT-CALL-LOG]::{isInProgressCall}',
          '[CHAT-CALL-LOG]::{isCompletedCall}:::::',
        ]
    }
    default:
      return ['', '']
  }
}

export const categorizeConversation = (
  conversation: IChatConversation[],
): Partial<ChatState> => {
  const response: Partial<ChatState> = {
    archived: [],
    inbox: [],
    data: [],
  }

  const archived = conversation.filter((el) => el.is_archived)
  const pinned_inbox = conversation.filter(
    (el) => !el.is_archived && el.is_pinned,
  )
  const unpinned_inbox = conversation.filter(
    (el) => !el.is_archived && !el.is_pinned,
  )
  if (archived.length > 0)
    response.archived = sortConversationByDate(
      archived,
      'date_time_archived_at',
    )

  if (pinned_inbox.length > 0 || unpinned_inbox.length > 0) {
    response.inbox = []
    if (pinned_inbox.length > 0)
      response.inbox = sortConversationByDate(pinned_inbox)
    if (unpinned_inbox.length > 0)
      response.inbox = [
        ...(response.inbox || []),
        ...sortConversationByDate(unpinned_inbox),
      ]
  }

  return response
}

export const getConversationChannel = (channel: ChatChannel): string => {
  if (channel === 'SMS') return 'SMS'
  else if (channel === 'Messenger') return 'Meta'
  else if (channel === 'Instagram') return 'Meta'
  else if (channel === 'In-app') return 'In-app'
  else return 'In-app'
}

export const getMetaChannel = (
  channel: ChatChannel,
): MetaChannel | undefined => {
  if (channel === 'Messenger') return MetaChannel.messenger
  else if (channel === 'Instagram') return MetaChannel.instagram
  else return undefined
}

export const extractUrlsFromHTML = (htmlContent: string): string[] => {
  const urls: string[] = []
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = htmlContent

  // Find all anchor tags
  const anchors = tempDiv.querySelectorAll('a[href]')
  anchors.forEach((anchor) => {
    const href = anchor.getAttribute('href')
    if (href) {
      try {
        // Validate URL
        const url = new URL(href, window.location.origin)
        // Only include http/https URLs
        if (url.protocol === 'http:' || url.protocol === 'https:') {
          urls.push(url.href)
        }
      } catch {
        logger.error('Invalid URL:', href)
      }
    }
  })

  // Check for plain text URLs in the content
  const textContent = tempDiv.textContent || ''
  const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`[\]]+[^\s<>"{}|\\^`[\].,;:!?])/gi
  const textUrls = textContent.match(urlRegex) || []
  textUrls.forEach((url) => {
    try {
      const urlObj = new URL(url)
      if (urlObj.protocol === 'http:' || urlObj.protocol === 'https:') {
        // Avoid duplicates
        if (!urls.includes(urlObj.href)) {
          urls.push(urlObj.href)
        }
      }
    } catch {
      logger.error('Invalid URL:', url)
    }
  })

  return urls
}

export const formatChatMessageTimestamp = (
  timestamp: Date | string | null | undefined,
): string => {
  if (!timestamp) return ''
  const messageTime = moment(timestamp)
  if (!messageTime.isValid()) return ''

  const now = moment()
  const time = messageTime.format('h:mm A')

  if (messageTime.isSame(now, 'day')) return time
  if (messageTime.isSame(now.clone().subtract(1, 'day'), 'day')) {
    return `Yesterday ${time}`
  }
  if (messageTime.isSame(now, 'week')) {
    return `${messageTime.format('dddd')} ${time}`
  }
  if (messageTime.isSame(now, 'year')) {
    return messageTime.format('MMM D, h:mm A')
  }
  return messageTime.format('MMM D, YYYY, h:mm A')
}

export const getInternalChatUnreadCount = (
  conversation: IChatConversation,
  currentUserId: string,
): number => {
  const participant = conversation.chat_participant?.find(
    (p) => p.user_id === currentUserId,
  )
  const lastReadAt = participant?.last_read_at
  const messages = conversation.chat_message ?? []
  if (!lastReadAt) return messages.length
  const lastReadTime = new Date(lastReadAt).getTime()
  return messages
    .filter((m) => m.sender_id !== currentUserId)
    .filter((m) => new Date(m.timestamp).getTime() > lastReadTime).length
}
